import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, Filter, Download, CreditCard as Edit, Trash2, Phone, Mail, Building2, User, Calendar, Brain, X, CheckSquare, Square, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, BookOpen } from 'lucide-react';
import type { Contact, Activity } from '../types';
import { contactsService } from '../services/supabaseService';
import { supabase } from '../services/supabaseService';
import ContactModal from './ContactModal';
import ContactSummaryAI from './ContactSummaryAI';
import ResponseSuggestionAI from './ResponseSuggestionAI';
import ClientIntelligencePanel from './intelligence/ClientIntelligencePanel';
import ClientDetailPage from './ClientDetailPage';
import { mockActivities } from '../data/mockData';
import { useNotifications } from '../hooks/useNotifications';
import { useDataSync, useDataSubscription } from '../contexts/DataSyncContext';
import { useDataLoader } from '../hooks/useDataLoader';
import { useToast } from '../hooks/useToast';
import authService from '../services/authService';

const ContactList: React.FC = () => {
  const { notifyContactCreated } = useNotifications();
  const { notifyDataChange } = useDataSync();
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContactForAI, setSelectedContactForAI] = useState<Contact | null>(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [selectedContactForIntel, setSelectedContactForIntel] = useState<Contact | null>(null);
  const [detailContact, setDetailContact] = useState<Contact | null>(null);
  const [contactActivities, setContactActivities] = useState<Activity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  // Handle ESC key to close AI panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAIPanel) {
        setShowAIPanel(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAIPanel]);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');
  const [entityFilter, setEntityFilter] = useState<'all' | 'person' | 'company'>('all');
  const [filters, setFilters] = useState({
    leadStatus: '',
    owner: '',
    productInterest: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [relationshipInfo, setRelationshipInfo] = useState<any>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [showBulkConfirmation, setShowBulkConfirmation] = useState(false);
  const [bulkRelationshipInfo, setBulkRelationshipInfo] = useState<any[]>([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { toast, showToast, clearToast } = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { data: contacts, loading, isRevalidating, refetch, invalidate, isRealtimeConnected } = useDataLoader<Contact[]>(
    `contacts-list-${activeTab}-${entityFilter}`,
    () => activeTab === 'active'
      ? contactsService.getAll(false, entityFilter === 'all' ? undefined : entityFilter)
      : contactsService.getInactive(),
    {
      ttl: 60,
      realtimeTable: 'contacts',
      realtimeEnabled: true,
      staleWhileRevalidate: true,
      onSuccess: (data) => {
        if (import.meta.env.DEV) console.log(`✅ ContactList: ${data.length} contactos cargados (Realtime: ${isRealtimeConnected ? 'ON' : 'OFF'})`);
      },
      onRealtimeEvent: (event) => {
        if (import.meta.env.DEV) console.log(`🔔 ContactList: Realtime event -`, event.type);
        notifyDataChange('contacts', event.type === 'INSERT' ? 'create' : event.type === 'UPDATE' ? 'update' : 'delete', event.record?.id);
      }
    }
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (contacts) {
      applyFilters(searchTerm, filters);
    }
  }, [contacts, searchTerm, filters]);

  useEffect(() => {
    setSelectedContactIds(new Set());
    setCurrentPage(1); // Reset to first page when changing tabs
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, filters]);

  const leadStatusColors = {
    nuevo: 'bg-blue-100 text-blue-800',
    en_seguimiento: 'bg-yellow-100 text-yellow-800',
    cerrado: 'bg-green-100 text-green-800',
    descartado: 'bg-red-100 text-red-800',
    ya_es_cliente: 'bg-purple-100 text-purple-800'
  };

  const leadStatusLabels = {
    nuevo: 'Nuevo',
    en_seguimiento: 'En Seguimiento',
    cerrado: 'Cerrado',
    descartado: 'Descartado',
    ya_es_cliente: 'Ya es Cliente'
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    applyFilters(term, filters);
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    applyFilters(searchTerm, newFilters);
  };

  const applyFilters = useCallback((search: string, currentFilters: typeof filters) => {
    if (!contacts) {
      setFilteredContacts([]);
      return;
    }

    let filtered = contacts.filter(contact => {
      const matchesSearch = !search ||
        contact.fullName.toLowerCase().includes(search.toLowerCase()) ||
        contact.email.toLowerCase().includes(search.toLowerCase()) ||
        contact.company.toLowerCase().includes(search.toLowerCase()) ||
        contact.phone.includes(search);

      const matchesStatus = !currentFilters.leadStatus || contact.leadStatus === currentFilters.leadStatus;
      const matchesOwner = !currentFilters.owner || contact.owner === currentFilters.owner;
      const matchesProduct = !currentFilters.productInterest || contact.productInterest === currentFilters.productInterest;

      return matchesSearch && matchesStatus && matchesOwner && matchesProduct;
    });

    setFilteredContacts(filtered);
  }, [contacts]);

  // Pagination calculations
  const paginationData = useMemo(() => {
    const totalItems = filteredContacts.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedContacts = filteredContacts.slice(startIndex, endIndex);

    return {
      totalItems,
      totalPages,
      startIndex,
      endIndex,
      paginatedContacts,
      showingFrom: startIndex + 1,
      showingTo: Math.min(endIndex, totalItems)
    };
  }, [filteredContacts, currentPage, itemsPerPage]);

  // Pagination controls
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, paginationData.totalPages)));
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(paginationData.totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  const handleSaveContact = async (contactData: Partial<Contact>) => {
    try {
    if (editingContact) {
      // Update existing contact
      await contactsService.update(editingContact.id, contactData);
      invalidate();
      await refetch();

      notifyDataChange('contacts', 'update', editingContact.id);
    } else {
      // Create new contact / company
      const newContact = await contactsService.create({
        fullName: contactData.fullName || '',
        email: contactData.email || '',
        phone: contactData.phone || '',
        company: contactData.company || '',
        role: contactData.role,
        leadStatus: contactData.leadStatus || 'nuevo',
        productInterest: contactData.productInterest || '',
        owner: contactData.owner || authService.getCurrentSession()?.user?.name || '',
        notes: contactData.notes || '',
        entityType: contactData.entityType,
        parentId: contactData.parentId,
        website: contactData.website,
        logo: contactData.logo,
      });
      invalidate();
      await refetch();

      notifyDataChange('contacts', 'create', newContact.id);

      // Send real notification for new contact
      await notifyContactCreated(
        newContact.fullName,
        newContact.company,
        undefined // Broadcast to all users
      );
    }
    setIsModalOpen(false);
    setEditingContact(null);
    } catch (error) {
      console.error('Error saving contact:', error);
      showToast('Error al guardar el contacto', 'error');
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    try {
      // Primero, verificar si tiene relaciones
      const relationships = await contactsService.checkRelationships(contactId);
      setRelationshipInfo(relationships);
      setContactToDelete(contactId);
      setShowDeleteConfirmation(true);
    } catch (error) {
      console.error('Error checking contact relationships:', error);
      showToast('Error al verificar las relaciones del contacto', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;

    try {
      // Soft delete - marcar como inactivo
      await contactsService.delete(contactToDelete);
      setShowDeleteConfirmation(false);
      setContactToDelete(null);
      setRelationshipInfo(null);
      invalidate();
      await refetch();

      notifyDataChange('contacts', 'delete', contactToDelete);
      showToast('Contacto marcado como inactivo correctamente');
    } catch (error) {
      console.error('Error deleting contact:', error);
      showToast('Error al desactivar el contacto', 'error');
    }
  };

  const toggleContactSelection = (contactId: string) => {
    setSelectedContactIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    // Only select/deselect items on current page
    const currentPageIds = paginationData.paginatedContacts.map(c => c.id);
    const allCurrentPageSelected = currentPageIds.every(id => selectedContactIds.has(id));

    if (allCurrentPageSelected) {
      // Deselect all on current page
      setSelectedContactIds(prev => {
        const newSet = new Set(prev);
        currentPageIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all on current page
      setSelectedContactIds(prev => {
        const newSet = new Set(prev);
        currentPageIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContactIds.size === 0) return;

    try {
      const relationshipsPromises = Array.from(selectedContactIds).map(id =>
        contactsService.checkRelationships(id).then(rel => ({ id, ...rel }))
      );
      const relationships = await Promise.all(relationshipsPromises);
      setBulkRelationshipInfo(relationships);
      setShowBulkConfirmation(true);
    } catch (error) {
      console.error('Error checking bulk relationships:', error);
      showToast('Error al verificar las relaciones de los contactos', 'error');
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedContactIds.size === 0) return;

    setIsBulkProcessing(true);
    const errors: string[] = [];
    const succeeded: string[] = [];

    try {
      for (const contactId of Array.from(selectedContactIds)) {
        try {
          await contactsService.delete(contactId);
          succeeded.push(contactId);
          notifyDataChange('contacts', 'delete', contactId);
        } catch (error) {
          console.error(`Error deleting contact ${contactId}:`, error);
          errors.push(contactId);
        }
      }

      setShowBulkConfirmation(false);
      setBulkRelationshipInfo([]);
      setSelectedContactIds(new Set());
      setIsBulkProcessing(false);

      invalidate();
      await refetch();

      if (errors.length === 0) {
        showToast(`${succeeded.length} contacto(s) marcado(s) como inactivo correctamente`);
      } else {
        showToast(`${succeeded.length} desactivado(s). ${errors.length} no pudieron ser desactivados.`, 'error');
      }
    } catch (error) {
      console.error('Error in bulk delete:', error);
      setIsBulkProcessing(false);
      showToast('Error al desactivar los contactos', 'error');
    }
  };

  const handleRestoreContact = async (contactId: string) => {
    try {
      await contactsService.restore(contactId);
      invalidate();
      await refetch();
      notifyDataChange('contacts', 'update', contactId);
      showToast('Contacto restaurado correctamente');
    } catch (error) {
      console.error('Error restoring contact:', error);
      showToast('Error al restaurar el contacto', 'error');
    }
  };

  const exportToCSV = () => {
    const headers = ['Nombre', 'Email', 'Teléfono', 'Empresa', 'Estado', 'Producto de Interés', 'Propietario'];
    const csvContent = [
      headers.join(','),
      ...filteredContacts.map(contact => [
        contact.fullName,
        contact.email,
        contact.phone,
        contact.company,
        leadStatusLabels[contact.leadStatus],
        contact.productInterest,
        contact.owner
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contactos.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleShowAI = async (contact: Contact) => {
    setSelectedContactForAI(contact);
    setShowAIPanel(true);

    // Load real activities for this contact
    setLoadingActivities(true);
    try {
      const { data: activities, error } = await supabase
        .from('activities')
        .select('*')
        .eq('contact_id', contact.id)
        .order('datetime', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error loading activities:', error);
        // Fallback to mock activities
        setContactActivities(mockActivities.filter(a => a.contactId === contact.id));
      } else {
        // Transform database format to app format
        const formattedActivities: Activity[] = (activities || []).map(a => ({
          id: a.id,
          contactId: a.contact_id,
          leadId: a.lead_id,
          type: a.type,
          description: a.description,
          datetime: new Date(a.datetime),
          userId: a.user_id
        }));
        setContactActivities(formattedActivities);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
      // Fallback to mock activities
      setContactActivities(mockActivities.filter(a => a.contactId === contact.id));
    } finally {
      setLoadingActivities(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando contactos...</span>
      </div>
    );
  }

  // Show detail page if a contact is selected
  if (detailContact) {
    return (
      <ClientDetailPage
        contact={detailContact}
        onBack={() => setDetailContact(null)}
        onContactUpdated={() => {
          setDetailContact(null);
          notifyDataChange('contacts');
        }}
        onNavigate={(next) => setDetailContact(next)}
      />
    );
  }

  return (
    <div className="p-6 flex gap-6">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <span>{toast.message}</span>
          <button onClick={clearToast} className="ml-2 opacity-75 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className={`transition-all duration-300 ${showAIPanel ? 'w-2/3' : 'w-full'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
            {isRevalidating && (
              <div className="flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                <span className="text-xs">Actualizando...</span>
              </div>
            )}
          </div>
          <p className="text-gray-600">Gestiona tus clientes y leads</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Exportar CSV</span>
          </button>
          <button
            onClick={() => {
              setEditingContact(null);
              setIsModalOpen(true);
            }}
            className={`flex items-center space-x-2 px-4 py-2 text-white rounded-lg transition-colors ${
              entityFilter === 'company' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            <Plus className="w-4 h-4" />
            <span>{entityFilter === 'company' ? 'Nueva Empresa' : 'Nuevo Cliente'}</span>
          </button>
        </div>
      </div>

      {/* Tabs + filtro persona/empresa */}
      <div className="mb-6 flex items-end justify-between gap-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`${
              activeTab === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Activos
            {contacts && activeTab === 'active' && (
              <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-blue-100 text-blue-600">
                {contacts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('inactive')}
            className={`${
              activeTab === 'inactive'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Inactivos
            {contacts && activeTab === 'inactive' && (
              <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-600">
                {contacts.length}
              </span>
            )}
          </button>
        </nav>
        {activeTab === 'active' && (
          <div className="flex gap-2 pb-3">
            {(['all', 'person', 'company'] as const).map((k) => (
              <button
                key={k}
                onClick={() => setEntityFilter(k)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  entityFilter === k
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {k === 'person' && <User className="w-3.5 h-3.5" />}
                {k === 'company' && <Building2 className="w-3.5 h-3.5" />}
                <span>
                  {k === 'all' ? 'Todos' : k === 'person' ? 'Personas' : 'Empresas'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <input
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filters.leadStatus}
            onChange={(e) => handleFilterChange({ ...filters, leadStatus: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos los estados</option>
            <option value="nuevo">Nuevo</option>
            <option value="en_seguimiento">En Seguimiento</option>
            <option value="cerrado">Cerrado</option>
            <option value="descartado">Descartado</option>
          </select>
          <select
            value={filters.owner}
            onChange={(e) => handleFilterChange({ ...filters, owner: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos los responsables</option>
            {/* Owners derivados de los contacts cargados. Antes había 3 nombres
                hardcoded (Ana García/Carlos López/María Rodríguez) que no
                coincidían con los usuarios reales del CRM. */}
            {Array.from(new Set((contacts || []).map((c: Contact) => c.owner).filter(Boolean)))
              .sort()
              .map(owner => (
                <option key={owner} value={owner}>{owner}</option>
              ))}
          </select>
          <select
            value={filters.productInterest}
            onChange={(e) => handleFilterChange({ ...filters, productInterest: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos los productos</option>
            <option value="Desarrollo Web">Desarrollo Web</option>
            <option value="Branding y Marketing">Branding y Marketing</option>
            <option value="E-commerce">E-commerce</option>
          </select>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {activeTab === 'active' && (
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={toggleSelectAll}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                      title={
                        paginationData.paginatedContacts.every(c => selectedContactIds.has(c.id))
                          ? 'Deseleccionar página'
                          : 'Seleccionar página'
                      }
                    >
                      {paginationData.paginatedContacts.length > 0 &&
                      paginationData.paginatedContacts.every(c => selectedContactIds.has(c.id)) ? (
                        <CheckSquare className="w-5 h-5" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Producto de Interés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Responsable
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Última Actividad
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginationData.paginatedContacts.map((contact) => (
                <tr
                  key={contact.id}
                  className={`hover:bg-gray-50 transition-colors ${
                    selectedContactIds.has(contact.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  {activeTab === 'active' && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleContactSelection(contact.id)}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {selectedContactIds.has(contact.id) ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            contact.entityType === 'company' ? 'bg-purple-100' : 'bg-blue-100'
                          }`}
                          title={contact.entityType === 'company' ? 'Empresa' : 'Persona'}
                        >
                          {contact.entityType === 'company' ? (
                            <Building2 className="h-5 w-5 text-purple-600" />
                          ) : (
                            <User className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => setDetailContact(contact)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                        >
                          {contact.fullName}
                        </button>
                        {contact.email && (
                          <div className="text-sm text-gray-500 flex items-center space-x-2">
                            <Mail className="w-3 h-3" />
                            <span>{contact.email}</span>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="text-sm text-gray-500 flex items-center space-x-2">
                            <Phone className="w-3 h-3" />
                            <span>{contact.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{contact.company}</div>
                        {contact.role && (
                          <div className="text-sm text-gray-500">{contact.role}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${leadStatusColors[contact.leadStatus]}`}>
                      {leadStatusLabels[contact.leadStatus]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contact.productInterest}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contact.owner}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.lastActivity.toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {activeTab === 'active' ? (
                        <>
                          <button
                            onClick={() => handleShowAI(contact)}
                            className="text-purple-600 hover:text-purple-900 p-1 rounded"
                            title="Análisis IA"
                          >
                            <Brain className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setSelectedContactForIntel(contact)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Inteligencia del Cliente"
                          >
                            <BookOpen className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingContact(contact);
                              setIsModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteContact(contact.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Desactivar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestoreContact(contact.id)}
                          className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                          title="Restaurar contacto"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span>Restaurar</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {paginationData.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{paginationData.showingFrom}</span> a{' '}
              <span className="font-medium">{paginationData.showingTo}</span> de{' '}
              <span className="font-medium">{paginationData.totalItems}</span> clientes
            </div>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={10}>10 por página</option>
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Primera página"
            >
              <ChevronsLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Página anterior"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex items-center space-x-1">
              {[...Array(paginationData.totalPages)].map((_, index) => {
                const page = index + 1;
                const showPage =
                  page === 1 ||
                  page === paginationData.totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1);

                const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                const showEllipsisAfter = page === currentPage + 2 && currentPage < paginationData.totalPages - 2;

                if (!showPage && !showEllipsisBefore && !showEllipsisAfter) {
                  return null;
                }

                if (showEllipsisBefore || showEllipsisAfter) {
                  return (
                    <span key={`ellipsis-${page}`} className="px-3 py-1 text-gray-500">
                      ...
                    </span>
                  );
                }

                return (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`min-w-[2.5rem] px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={goToNextPage}
              disabled={currentPage === paginationData.totalPages}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Página siguiente"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={goToLastPage}
              disabled={currentPage === paginationData.totalPages}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Última página"
            >
              <ChevronsRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* Simple summary when no pagination needed */}
      {paginationData.totalPages <= 1 && (
        <div className="mt-4 text-sm text-gray-500">
          Mostrando {paginationData.totalItems} cliente{paginationData.totalItems !== 1 ? 's' : ''}
        </div>
      )}

      {/* Floating Action Bar for Bulk Operations */}
      {selectedContactIds.size > 0 && activeTab === 'active' && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-2xl border border-gray-200 px-6 py-4 z-40 flex items-center space-x-4 animate-slide-up">
          <div className="flex items-center space-x-2">
            <CheckSquare className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-gray-900">
              {selectedContactIds.size} cliente{selectedContactIds.size !== 1 ? 's' : ''} seleccionado{selectedContactIds.size !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="h-6 w-px bg-gray-300"></div>
          <button
            onClick={() => setSelectedContactIds(new Set())}
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleBulkDelete}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>Desactivar seleccionados</span>
          </button>
        </div>
      )}

      </div>

      {/* AI Panel */}
      {showAIPanel && selectedContactForAI && (
        <div className="w-1/3 space-y-6 border-l-2 border-purple-200 pl-6 animate-slide-in-right">
          <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg shadow-sm sticky top-0 z-10">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Análisis IA</h2>
                <p className="text-xs text-gray-500">Para: {selectedContactForAI.fullName}</p>
              </div>
            </div>
            <button
              onClick={() => setShowAIPanel(false)}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-white rounded-lg transition-all duration-200 hover:shadow-md"
              title="Cerrar panel (ESC)"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <ContactSummaryAI
            contact={selectedContactForAI}
            activities={loadingActivities ? [] : contactActivities}
          />
          
          <ResponseSuggestionAI 
            contact={selectedContactForAI}
            context="follow_up"
            onUseResponse={(response) => {
              if (import.meta.env.DEV) console.log('Using AI response:', response);
              // Here you could open an email client or copy to clipboard
            }}
          />
        </div>
      )}

      {/* Contact Modal — al crear, pre-seleccionamos empresa si el filtro está en 'company' */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingContact(null);
        }}
        onSave={handleSaveContact}
        contact={editingContact}
        defaultEntityType={entityFilter === 'company' ? 'company' : 'person'}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && relationshipInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmar Desactivación</h3>
              </div>

              <p className="text-gray-600 mb-4">
                ¿Estás seguro de que quieres desactivar este contacto? El contacto se moverá a la pestaña "Inactivos" y podrás restaurarlo cuando quieras.
              </p>

              {(relationshipInfo.hasProjects || relationshipInfo.hasMeetings || relationshipInfo.hasActivities) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Información importante:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {relationshipInfo.hasProjects && (
                      <li>• {relationshipInfo.projectsCount} proyecto(s) asociado(s) - Los datos se preservarán</li>
                    )}
                    {relationshipInfo.hasMeetings && (
                      <li>• {relationshipInfo.meetingsCount} reunión(es) asociada(s)</li>
                    )}
                    {relationshipInfo.hasActivities && (
                      <li>• {relationshipInfo.activitiesCount} actividad(es) asociada(s)</li>
                    )}
                  </ul>
                  <p className="text-xs text-blue-700 mt-2">
                    Toda la información histórica se mantendrá intacta.
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setContactToDelete(null);
                    setRelationshipInfo(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Desactivar Contacto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkConfirmation && bulkRelationshipInfo.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmar Desactivación Masiva</h3>
              </div>

              <p className="text-gray-600 mb-4">
                ¿Estás seguro de que quieres desactivar <strong>{selectedContactIds.size} cliente{selectedContactIds.size !== 1 ? 's' : ''}</strong>?
                Los contactos se moverán a la pestaña "Inactivos\" y podrás restaurarlos cuando quieras.
              </p>

              {bulkRelationshipInfo.some(r => r.hasProjects || r.hasMeetings || r.hasActivities) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Resumen de relaciones:</h4>

                  <div className="space-y-4">
                    {bulkRelationshipInfo.filter(r => r.hasProjects || r.hasMeetings || r.hasActivities).map((info) => {
                      const contact = filteredContacts.find(c => c.id === info.id);
                      return (
                        <div key={info.id} className="bg-white rounded p-3 border border-blue-100">
                          <div className="font-medium text-gray-900 mb-2">{contact?.fullName || 'Contacto'}</div>
                          <ul className="text-sm text-blue-800 space-y-1">
                            {info.hasProjects && (
                              <li>• {info.projectsCount} proyecto(s) asociado(s)</li>
                            )}
                            {info.hasMeetings && (
                              <li>• {info.meetingsCount} reunión(es) asociada(s)</li>
                            )}
                            {info.hasActivities && (
                              <li>• {info.activitiesCount} actividad(es) asociada(s)</li>
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-blue-700 mt-3">
                    Toda la información histórica se mantendrá intacta.
                  </p>
                </div>
              )}

              {isBulkProcessing && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-700">Procesando desactivaciones...</span>
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowBulkConfirmation(false);
                    setBulkRelationshipInfo([]);
                  }}
                  disabled={isBulkProcessing}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmBulkDelete}
                  disabled={isBulkProcessing}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBulkProcessing ? 'Procesando...' : `Desactivar ${selectedContactIds.size} Contacto${selectedContactIds.size !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Intelligence Panel */}
      {selectedContactForIntel && (
        <ClientIntelligencePanel
          isOpen={!!selectedContactForIntel}
          onClose={() => setSelectedContactForIntel(null)}
          contact={selectedContactForIntel}
        />
      )}
    </div>
  );
};

export default ContactList;